import { Grid } from '@material-ui/core';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useRifm } from 'rifm';
import {
  restorePage,
  setHomePage,
} from 'src/common/components/emerald-home-page/emerald-home-page';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import CashierPreferencesHelper from 'src/common/helpers/cashier-preferences-helper';
import {
  LOCAL_CURRENCY_SYMBOL,
  LOCAL_ISO_CURRENCY_CODE,
} from 'src/common/helpers/localized-description-helper';
import { isNumber } from 'src/common/helpers/number-helper';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { ITender } from 'src/common/interfaces/dtos/ITender';
import { IButton } from 'src/common/interfaces/IButton';
import { InputField } from 'src/common/interfaces/InputField';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';
import { cancelUnPaidPreTender } from 'src/home-page/business-logic/cancel-tender-payment';
import { RifmOptions } from 'src/keypad/keypad-operation';
import { Pagination } from 'src/pagination/pagination';
import { TenderParameters } from 'src/tendering/interfaces/tender-parameters';
import { submitPayment } from '../../business-logic/handle-tender-payment';
import { exitTenderingMode } from '../../business-logic/tendering-actions';
import KeypadColumn from '../keypad-column';
import SuggestedAmountButtons from '../suggested-amount-buttons';
import TenderAmountDue from '../tender-amount-due';
import TenderGroupTitle from '../tender-group-title';
import { styles } from '../tender-group-view-styles';
import TenderViewButtons from '../tender-view-buttons';
import FormInputView from './form-input-view';

export const showProcessTender = (props: ProcessTenderProps): void => {
  setHomePage(<ProcessTender {...props} />);
};

const isLeftHanded = CashierPreferencesHelper.isLeftHanded();

type Props = ProcessTenderProps & ProcessTenderStates;

const ProcessTenderPage: React.FC<Props> = ({
  defaultValue,
  amountDue,
  selectedTender,
  trainingModeActive,
  suggestedAmounts,
}: Props): JSX.Element => {
  const maxLength = 12;
  const { t } = useTranslation();
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();

  const [inputDisabled, setInputDisabled] = useState<boolean>(false);

  const [currentAmount, setCurrentAmount] = useState<string>(
    `${isNumber(defaultValue) ? defaultValue?.toFixed(2) : ''}`,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const rifmOptions = RifmOptions.price;
  const rifm = useRifm(rifmOptions(currentAmount, setCurrentAmount, inputRef));

  const [inputFields, setInputFields] = useState<TenderParameters[]>([]);
  const [keyboardId, setKeyboardId] = useState('');

  const amountInputField: InputField = useMemo(() => {
    const selectedTenderCurrencySymbol = selectedTender.currency?.symbol || LOCAL_CURRENCY_SYMBOL;
    return {
      id: 'amount',
      label: `${t('Amount')} (${selectedTenderCurrencySymbol})`,
      autoFocus: true,
      name: 'current-amount',
      value: rifm.value,
      onChange: rifm.onChange,
      inputRef: inputRef,
      helperText:
        +rifm.value > 0
          ? ''
          : `${selectedTender.description} ${t('amount-is-required-helper-text')}`,
    };
  }, [rifm.onChange, rifm.value, selectedTender, t]);

  const inputCollection = useMemo((): InputField[] => {
    return [amountInputField];
  }, [amountInputField]);

  const handleOnFocus = (event): void => {
    setKeyboardId(event.target.id);
  };

  const handleInputChange = (event): void => {
    const span = createSpan('Tender View - Amount Entered');
    const sanitizedValidationFieldText = (text: string) => text.replace(/[^0-9]/g, '');
    if (event.target.name === 'current-amount') {
      if (event.target.value.length < maxLength) {
        setCurrentAmount(event.target.value);
      }
    } else {
      const updatedValues: TenderParameters[] = [...inputFields];
      updatedValues[event.target.dataset.id]['value'] = sanitizedValidationFieldText(
        event.target.value,
      );
      updatedValues[event.target.dataset.id]['validationMessage'] = '';
      setInputFields(updatedValues);
    }
    endSpan(span);
  };

  const handleInputPayment = async (): Promise<void> => {
    const span = createSpan(`Tender View - Amount ${currentAmount} submited`);
    setInputDisabled(true);

    if (+currentAmount <= 0) {
      endSpan(span);
      return;
    }

    await submitPayment(Number(currentAmount), EntryMethod.Keyed, selectedTender);

    endSpan(span);
  };

  const submitButton: IButton = {
    label: 'OK',
    onClick: handleInputPayment,
    testId: 'submit-keypad-button',
  };

  return (
    <Grid
      item
      container
      direction='row'
      justifyContent='space-between'
      className={`fullHeight flexFull ${classes.processTenderContainer} ${
        trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
      }`}
    >
      {trainingModeActive && (
        <TrainingModeWatermark
          dataTestId='process-tender-training-mode-watermark'
          supplementalStyles={trainingModeClasses.fullMiddleAreaTrainingWaterMark}
        />
      )}
      <Grid
        item
        container
        direction='row'
        justifyContent='space-between'
        className={`${classes.processTenderTopRow}`}
      >
        <Grid item xs={8}>
          <TenderGroupTitle title={selectedTender.description || ''} />
        </Grid>
        <Grid item xs={4}>
          <TenderAmountDue amountDue={amountDue} align='right' />
        </Grid>
      </Grid>

      {!isLeftHanded && (
        <Grid item container direction='row' style={{ paddingBottom: '1vh' }} alignItems='flex-end'>
          <TendersColumn
            selectedTender={selectedTender}
            suggestedAmounts={suggestedAmounts}
            setCurrentAmount={handleInputChange}
          />
          <FormColumn
            disabled={inputDisabled}
            handleInputPayment={handleInputPayment}
            handleInputChange={handleInputChange}
            handleOnFocus={handleOnFocus}
            inputCollection={inputCollection}
          />
          <KeypadColumn
            keypadSubmitButton={submitButton}
            headerText={t('TenderManualAmountEntryKeypad')}
            keyboardId={keyboardId}
            isKeypadVisible={true}
            isLeftHanded={isLeftHanded}
            formatter={RifmOptions.price}
            disableQuantity={true}
          />
        </Grid>
      )}

      {isLeftHanded && (
        <Grid item container direction='row' style={{ paddingBottom: '1vh' }} alignItems='flex-end'>
          <KeypadColumn
            keypadSubmitButton={submitButton}
            headerText={t('TenderManualAmountEntryKeypad')}
            keyboardId={keyboardId}
            isKeypadVisible={true}
            isLeftHanded={isLeftHanded}
            formatter={RifmOptions.price}
            disableQuantity={true}
          />
          <FormColumn
            disabled={inputDisabled}
            handleInputPayment={handleInputPayment}
            handleInputChange={handleInputChange}
            handleOnFocus={handleOnFocus}
            inputCollection={inputCollection}
          />
          <TendersColumn
            selectedTender={selectedTender}
            suggestedAmounts={suggestedAmounts}
            setCurrentAmount={handleInputChange}
          />
        </Grid>
      )}
    </Grid>
  );
};

interface ProcessTenderProps {
  defaultValue?: number;
  selectedTender: ITender;
  suggestedAmounts?: number[];
}

interface ProcessTenderStates {
  amountDue: number;
  trainingModeActive: boolean;
}
const mapStateToProps = (state: AppState): ProcessTenderStates => ({
  amountDue: state.itemList.amountDue,
  trainingModeActive: state.posMode.trainingMode,
});

const ProcessTender = connect(mapStateToProps)(ProcessTenderPage);
export default ProcessTender;

interface TendersColumnProps {
  selectedTender: ITender;
  suggestedAmounts?: number[];
  setCurrentAmount: any;
}

const TendersColumn = ({
  selectedTender,
  suggestedAmounts,
  setCurrentAmount,
}: TendersColumnProps): JSX.Element => {
  const classes = styles();
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();
  const currencyCode = selectedTender.currency?.isoSymbol || LOCAL_ISO_CURRENCY_CODE;

  return (
    <Grid
      item
      container
      style={{
        paddingLeft: isLeftHanded ? '6vh' : '2vh',
        paddingBottom: '1.85vh',
        maxHeight: '67vh',
      }}
      xs={2}
      direction='column'
      className={'fullHeight'}
    >
      <Grid item className={classes.tenderButtons}>
        <div ref={scrollRef} className={'fullHeight verticalScroll'}>
          <SuggestedAmountButtons
            suggestedAmounts={suggestedAmounts}
            disabled={false}
            currencyCode={currencyCode}
            updateCurrentAmount={setCurrentAmount}
            selectedTender={selectedTender}
          />
        </div>
      </Grid>
      <Grid container className={classes.navButtons} justifyContent='space-evenly'>
        <Pagination
          currentPage={currentPage}
          totalPages={lastPage}
          updateCurrentPageHandler={onPageChanged}
          displayText={false}
          testIdPrefix={'process-tender'}
        />
      </Grid>
    </Grid>
  );
};
interface FormColumnProps {
  handleInputPayment: () => void;
  handleInputChange: (e) => void;
  handleOnFocus: (e) => void;
  disabled: boolean;
  inputCollection: InputField[];
}

const FormColumn = ({
  handleInputPayment,
  handleInputChange,
  handleOnFocus,
  disabled,
  inputCollection,
}: FormColumnProps): JSX.Element => {
  const { t } = useTranslation();
  const classes = styles();
  const maxLength = 12;

  const cancelHandler = (): void => {
    const span = createSpan('Tender View Cancel Button');
    restorePage();
    cancelUnPaidPreTender();
    endSpan(span);
  };

  const exitHandler = async (): Promise<void> => {
    const span = createSpan('Tender View Exit Button');
    await exitTenderingMode();
    endSpan(span);
  };

  return (
    <>
      <Grid
        item
        container
        md={7}
        style={{ alignItems: 'center', justifyContent: 'space-between', alignSelf: 'stretch' }}
        direction='column'
      >
        <FormInputView
          disabled={disabled}
          handleInputPayment={handleInputPayment}
          handleInputChange={handleInputChange}
          handleOnFocus={handleOnFocus}
          inputCollection={inputCollection}
          dataTestId='process-tender'
          containerClass={
            inputCollection && inputCollection.length === 1 ? classes.singleFieldTopMargin : ''
          }
          maxLength={maxLength}
        />

        <Grid item style={{ paddingBottom: '1vh' }}>
          <TenderViewButtons
            secondaryButton={{
              onClick: () => cancelHandler(),
              label: t('Back'),
            }}
            primaryButton={{
              onClick: async () => await exitHandler(),
              label: t('TenderingView_Exit'),
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
